
import { defineComponent, PropType } from 'vue'
import { buildXYRecords, ChartDataSet } from '~/utils/charts/unovis'
import {
  buildHumanizedTooltips,
  buildYDomain,
  computeXTickFormat,
  getThemeColors
} from '~/utils/charts/zchart'
import baseColors from '~/utils/tailwind/colors'
import { DEFAULT_AXIS_OPTIONS, Marker, Region } from './constants'

const LazyZChartBar = () => import('./ZChartBar.vue')
const LazyZChartLine = () => import('./ZChartLine.vue')
const LazyZChartAxisMixed = () => import('./ZChartAxisMixed.vue')

export interface DataItem extends ChartDataSet {
  type?: string
}

type XYRecord = ReturnType<typeof buildXYRecords>[number]

export default defineComponent({
  name: 'ZChart',
  components: {
    LazyZChartBar,
    LazyZChartLine,
    LazyZChartAxisMixed
  },
  props: {
    dataSets: {
      type: Array as PropType<Array<DataItem>>,
      required: true
    },
    labels: {
      type: Array as PropType<Array<string>>,
      default: () => []
    },
    yAxisMin: {
      type: Number as PropType<number | null>,
      default: null
    },
    yAxisMax: {
      type: Number as PropType<number | null>,
      default: null
    },
    type: {
      type: String,
      default: 'axis-mixed',
      validator: (value: string) => ['bar', 'line', 'axis-mixed'].includes(value)
    },
    height: {
      type: Number,
      default: 300
    },
    yMarkers: {
      type: Array as PropType<Array<Marker> | null>,
      default: () => null
    },
    yRegions: {
      type: Array as PropType<Array<Region> | null>,
      default: () => null
    },
    colors: {
      type: Array as PropType<Array<string>>,
      default: () => [
        'robin',
        'cherry',
        'aqua',
        'honey',
        'juniper',
        'lilac',
        'pink',
        'sea-glass',
        'robin'
      ]
    },
    barOptions: {
      type: Object,
      default: () => {
        return {
          barPadding: 0.5,
          stacked: 0
        }
      }
    },
    axisOptions: {
      type: Object,
      default: () => DEFAULT_AXIS_OPTIONS
    },
    tooltipOptions: {
      default: null,
      type: Object
    },
    humanizeTooltips: {
      default: true,
      type: Boolean
    }
  },
  data() {
    return {
      themeColors: {} as Record<string, string>
    }
  },
  created() {
    this.themeColors = getThemeColors(baseColors)
  },
  computed: {
    palette(): Array<string> {
      if (this.colors) {
        return (this.colors as Array<string>).map((token) => {
          return this.themeColors[token] || token
        })
      }
      return []
    },
    /** @return {Array<Marker>} */
    markers(): Array<Marker> {
      if (!this.yMarkers?.length) return []
      return (this.yMarkers as Array<Marker>).map((marker) => {
        if (!marker.options) {
          marker.options = {}
        }
        marker.options.lineType = marker.options.lineType ?? 'solid'
        if (marker.options.stroke && marker.options.stroke in this.themeColors) {
          marker.options.stroke = this.themeColors[marker.options.stroke]
        }
        return marker
      })
    },
    regions(): Array<Region> {
      if (!this.yRegions?.length) return []
      return (this.yRegions as Array<Region>).map((region) => {
        if (!region.options) {
          region.options = {}
        }

        const color = region.options.fill in this.themeColors ? region.options.fill : 'ink-300'
        region.options.fill = this.themeColors[color]
        region.options.stroke = this.themeColors[color]

        return region
      })
    },
    xTickFormat(): string {
      return computeXTickFormat(this.labels)
    },
    humanizedTooltips(): Record<
      string,
      (d: string | number, ...args: unknown[]) => string | number
    > {
      return buildHumanizedTooltips({
        humanizeTooltips: this.humanizeTooltips,
        tooltipOptions: this.tooltipOptions
      }) as Record<string, (d: string | number, ...args: unknown[]) => string | number>
    },
    xyRecords(): Array<XYRecord> {
      return buildXYRecords(this.labels, this.dataSets)
    },
    yDomain(): [number | undefined, number | undefined] | null {
      return buildYDomain(this.yAxisMin, this.yAxisMax)
    },
    childProps(): Record<string, unknown> {
      return {
        dataSets: this.dataSets,
        labels: this.labels,
        height: this.height,
        yMarkers: this.markers,
        yRegions: this.regions,
        palette: this.palette,
        barOptions: this.barOptions,
        axisOptions: this.axisOptions,
        tooltipOptions: this.tooltipOptions,
        humanizedTooltips: this.humanizedTooltips,
        xTickFormat: this.xTickFormat,
        yDomain: this.yDomain,
        xyRecords: this.xyRecords
      }
    }
  }
})
